import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import styles from './BillModificationHistory.module.css';
import { BILL_UPDATE_HISTORY } from '../constant/apiConstants';

const BillModificationHistory = () => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState('');

  useEffect(() => {
    fetchModificationHistory();
  }, []);

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');

    return {
      user: user ? JSON.parse(user) : null,
      token: token || null
    };
  };

  const fetchModificationHistory = async () => {
    try {
      setLoading(true);
      const { user, token } = getUserData();

      if (!user || !token) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${BILL_UPDATE_HISTORY}`, {
        user: user,
        token: token
      });

      if (response.data.status === 'success') {
        setHistoryData(response.data.payload || []);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (data, type) => {
    setModalData(data);
    setModalType(type);
  };

  const closeModal = () => {
    setModalData(null);
    setModalType('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseJsonSafely = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const renderItemsModal = () => {
    if (!modalData) return null;
    const items = parseJsonSafely(modalData);

    return (
      <div className={styles.modalOverlay} onClick={closeModal}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Bill Items Details</h2>
            <button className={styles.closeBtn} onClick={closeModal}>&times;</button>
          </div>
          <div className={styles.modalBody}>
            {items.length === 0 ? (
              <p>No items found</p>
            ) : (
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th>Barcode ID</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Size</th>
                    <th>Color</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Final Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.itemBarcodeID || 'N/A'}</td>
                      <td>{item.description || 'N/A'}</td>
                      <td>{item.itemCategory || 'N/A'}</td>
                      <td>{item.itemSize || 'N/A'}</td>
                      <td>{item.itemColor || 'N/A'}</td>
                      <td>{item.quantity || 0}</td>
                      <td>₹{item.price || 0}</td>
                      <td>{item.discount || 0}%</td>
                      <td>₹{item.final_amount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionsModal = () => {
    if (!modalData) return null;
    
    let transactions = [];
    let previousTransactions = [];
    let updatedTransactions = [];

    // Check if it's a transaction update (with previous and updated)
    try {
      const parsed = JSON.parse(modalData);
      if (parsed.previousTransaction && parsed.updatedTransactions) {
        previousTransactions = parsed.previousTransaction;
        updatedTransactions = parsed.updatedTransactions;
      } else {
        transactions = parseJsonSafely(modalData);
      }
    } catch {
      transactions = parseJsonSafely(modalData);
    }

    return (
      <div className={styles.modalOverlay} onClick={closeModal}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Transaction Details</h2>
            <button className={styles.closeBtn} onClick={closeModal}>&times;</button>
          </div>
          <div className={styles.modalBody}>
            {previousTransactions.length > 0 && (
              <>
                <h3 className={styles.sectionTitle}>Previous Transactions</h3>
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previousTransactions.map((txn, index) => (
                      <tr key={index}>
                        <td>{txn.transactionId || 'N/A'}</td>
                        <td>{txn.date || 'N/A'}</td>
                        <td>{txn.mode || 'N/A'}</td>
                        <td>{txn.type || 'N/A'}</td>
                        <td>₹{txn.amount || 0}</td>
                        <td>{txn.description || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h3 className={styles.sectionTitle}>Updated Transactions</h3>
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updatedTransactions.map((txn, index) => (
                      <tr key={index}>
                        <td>{txn.transactionId || 'N/A'}</td>
                        <td>{txn.date || 'N/A'}</td>
                        <td>{txn.mode || 'N/A'}</td>
                        <td>{txn.type || 'N/A'}</td>
                        <td>₹{txn.amount || 0}</td>
                        <td>{txn.description || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {transactions.length > 0 && (
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Date</th>
                  
                    
                    <th>Mode</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, index) => (
                    <tr key={index}>
                      <td>{txn.transactionId || 'N/A'}</td>
                      <td>{txn.date}</td>

                      <td>{txn.mode || 'N/A'}</td>
                      <td>{txn.type || 'N/A'}</td>
                      <td>₹{txn.amount || 0}</td>
                      <td>{txn.description || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {transactions.length === 0 && previousTransactions.length === 0 && (
              <p>No transactions found</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Bill Modification History</h1>
        <button className={styles.refreshBtn} onClick={fetchModificationHistory}>
          Refresh
        </button>
      </div>

      {historyData.length === 0 ? (
        <div className={styles.noData}>No modification history found</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Bill No</th>
                <th>Date</th>
                <th>Time</th>
                
                <th>Type</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Discount</th>
                <th>Payment Mode</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((record, index) => (
                <tr key={index}>
                  <td>{record.billNo}</td>
                   <td>{format(new Date(record.date), 'dd MMM yyyy')}</td>
                   <td>
                          {record.time
                      ? (() => {
                          const [h, m] = record.time.split(":");
                          const hour = ((+h + 11) % 12 + 1);
                          const ampm = +h >= 12 ? "PM" : "AM";
                          return `${hour.toString().padStart(2, "0")}:${m} ${ampm}`;
                        })()
                      : "-"}
                   </td>
                  <td>
                    <span className={`${styles.badge} ${
                      record.billModificationType === 'DELETE' 
                        ? styles.badgeDelete 
                        : styles.badgeUpdate
                    }`}>
                      {record.billModificationType}
                    </span>
                  </td>
                  <td>{record.customerDetails || 'N/A'}</td>
                  <td>₹{record.amount || 0}</td>
                  <td>{record.discount || 0}%</td>
                  <td>{record.paymentMode || 'N/A'}</td>
                  <td className={styles.description}>{record.description}</td>
                  <td>
                    <div className={styles.actionBtns}>
                      <button
                        className={styles.viewBtn}
                        onClick={() => openModal(record.billDetails, 'items')}
                      >
                        View Items
                      </button>
                      {record.transactionsDetails && (
                        <button
                          className={styles.viewBtn}
                          onClick={() => openModal(record.transactionsDetails, 'transactions')}
                        >
                          View Transactions
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalType === 'items' && renderItemsModal()}
      {modalType === 'transactions' && renderTransactionsModal()}
    </div>
  );
};

export default BillModificationHistory;